import { getItem, setItem, removeItem } from './localStorage'

/**
 * The three roles a user can hold in an agency.
 *
 * NB the read-only role's wire value is `ReadOnlyMember`, not `ReadOnly` — see
 * `userRoles` in `api/user/constants.ts`, whose key is the short form.
 */
export type TUserRole = 'Owner' | 'TeamMember' | 'ReadOnlyMember'

/** The agency the session is currently scoped to, as carried on the auth token. */
export type TAgencyMembership = {
  role: TUserRole
  agencyId: string
  agencyName: string
  segment?: {
    currentSegments?: string[]
    segmentMemberships?: string[]
  }
}

/**
 * The claims reOS puts on a JWT, as returned by `jwtDecode`.
 *
 * A refresh token carries the same envelope with `type: 'Refresh'` and only
 * `userId`/`agencyMembership` in `value`, which is why everything below
 * `userId` is optional.
 */
export type TDecodedAuthToken = {
  iss: string
  exp: number
  iat: number
  type: string
  value: {
    userId: string
    firstName?: string
    email?: string
    service?: string
    validSupportStaff?: boolean
    totpSecret?: string
    totpValidateAt?: number
    agencyMembership?: TAgencyMembership
  }
}

/** The token pair (plus stored `miscellaneous` blob) every auth endpoint responds with. */
export type TAuthTokens = {
  authToken: string
  refreshToken: string
  miscellaneous?: TUserMisc
}

/** Free-form per-user blob persisted to local storage alongside the tokens. */
export type TUserMisc = {
  registered?: boolean
  onboardingProgress?: Record<string, number>
  [key: string]: unknown
}

const TOKEN = '__TOKEN__'
const REFRESH_TOKEN = '__REFRESH_TOKEN__'
const MISC = '__MISC__'

export const setStorageItem = (name: string, value: string): void => setItem(name, value)
export const getStorageItem = (name: string): string | undefined => {
  const fetched = getItem(name)
  if (!fetched) {
    return undefined
  }
  return fetched
}

export const setStorageJSON = (name: string, value: unknown): void => setItem(name, JSON.stringify(value))
export const getStorageJSON = <T>(name: string): T | undefined => {
  const item = getStorageItem(name)
  if (!item) {
    return undefined
  }
  return JSON.parse(item)
}

export function deleteTokens(): void {
  removeItem(TOKEN)
  removeItem(REFRESH_TOKEN)
  removeItem(MISC)
}

// @todo improve logic
export const isAuthenticated = (): boolean => getStorageItem(TOKEN) !== undefined

export const getJWTToken = (): string | undefined => getStorageItem(TOKEN)

export const getAuthHeaders = (): { Authorization: string } => {
  return { Authorization: 'Bearer ' + getStorageItem(TOKEN) }
}

export const getJWTRefreshToken = (): string | undefined => getStorageItem(REFRESH_TOKEN)

export const getMisc = (): TUserMisc | undefined => getStorageJSON<TUserMisc>(MISC)

export const setMisc = (payload: TUserMisc): void => setStorageJSON(MISC, payload)

export function setTokensFromResponse(res: Partial<TAuthTokens>): void {
  const { authToken, refreshToken, miscellaneous } = res
  if (authToken !== undefined) {
    setStorageItem(TOKEN, authToken)
  }
  if (refreshToken !== undefined) {
    setStorageItem(REFRESH_TOKEN, refreshToken)
  }
  if (miscellaneous !== undefined) {
    setStorageJSON(MISC, miscellaneous)
  }
}

export function isTokenExpired(decodedToken: Pick<TDecodedAuthToken, 'exp'>): boolean {
  const { exp } = decodedToken
  const now = Math.floor(Date.now() / 1000)
  return !!exp && exp - now <= 0
}
